(function () {
class TabletopCommonsProgram extends HTMLElement {
  connectedCallback() {
    this.api = window.TtcEventsApi;
    const eventSlug = this.dataset.abblEventSlug?.trim();
    const apiBase = this.dataset.abblApiBase?.trim();
    this.eventsGrid = this.querySelector('[data-program-events-grid]');
    this.eventsNote = this.querySelector('[data-program-events-note]');
    this.tags = this.parseTags(this.dataset.abblFilterTags);
    this.tagMatch = this.dataset.tagMatch === 'any' ? 'any' : 'all';
    this.eventLimit = Math.max(3, Math.min(12, Number(this.dataset.eventLimit) || 6));
    this.store = null;
    this.weeklyEvents = [];

    if (!this.api || !apiBase) {
      this.setEventsState('Upcoming sessions are temporarily unavailable.');
      return;
    }

    if (eventSlug) this.loadEvent(apiBase, eventSlug);
    if (this.eventsGrid) {
      if (this.tags.length) this.loadFilteredEvents(apiBase);
      else this.setEventsState('Add Abbl filter tags to this page to show upcoming sessions.');
    }
  }

  async loadEvent(apiBase, eventSlug) {
    try {
      const [event, weekly] = await Promise.all([
        this.api.getEvent(apiBase, eventSlug),
        this.api.getWeekly(apiBase)
      ]);
      if (!event || Array.isArray(event)) throw new Error('Event response did not contain one event');
      this.weeklyEvents = weekly.events || [];
      this.store = weekly.store || this.store;
      this.renderEvent(event);
    } catch (error) {
      console.warn('Tabletop Commons event: live Abbl data unavailable.', error);
    }
  }

  async loadFilteredEvents(apiBase) {
    try {
      const [result, weekly] = await Promise.all([
        this.api.getUpcoming(apiBase, { pageSize: 100, maxPages: 10 }),
        this.api.getWeekly(apiBase)
      ]);
      this.store = result.store || this.store;
      this.weeklyEvents = weekly.events || [];
      const events = result.events
        .filter((event) => this.api.matchesTags(event, this.tags, this.tagMatch))
        .filter((event) => {
          const start = this.api.startDate(event);
          return !start || start.getTime() >= Date.now() - 86400000;
        })
        .sort((left, right) => (this.api.startDate(left)?.getTime() || Number.MAX_SAFE_INTEGER) - (this.api.startDate(right)?.getTime() || Number.MAX_SAFE_INTEGER))
        .slice(0, this.eventLimit);

      this.eventsGrid.innerHTML = '';
      if (events.length) {
        events.forEach((event) => this.eventsGrid.append(this.api.createEventCard(event, {
          store: this.store,
          label: this.api.eventLabel(event),
          weeklyEvents: this.weeklyEvents
        })));
        if (this.eventsNote) this.eventsNote.textContent = 'Dates, times and prices are supplied live by Abbl.';
      } else {
        this.setEventsState('No upcoming sessions are listed right now.');
      }
    } catch (error) {
      console.warn('Tabletop Commons program: live sessions unavailable.', error);
      this.setEventsState('Upcoming sessions are temporarily unavailable.');
    }
  }

  parseTags(value) {
    if (!value || !this.api) return [];
    try {
      return this.api.normaliseTags(JSON.parse(value));
    } catch (_) {
      return this.api.normaliseTags(String(value).split(','));
    }
  }

  setEventsState(message) {
    if (this.eventsGrid) this.eventsGrid.innerHTML = `<div class="ttc-events__empty"><strong>${this.api?.escapeHtml(message) || message}</strong></div>`;
    if (this.eventsNote) this.eventsNote.textContent = message;
  }

  renderEvent(event) {
    const name = this.api.first(event?.name, event?.Name, event?.title, event?.Title);
    const lead = this.api.first(event?.shortDescription, event?.ShortDescription, event?.summary, event?.Summary);
    const description = this.api.richTextValue(this.api.first(
      event?.descriptionHtml,
      event?.DescriptionHtml,
      event?.description,
      event?.Description,
      event?.richText,
      event?.RichText
    ));
    const heroImage = this.api.imageUrl(this.api.first(
      event?.heroImage,
      event?.HeroImage,
      event?.heroImageUrl,
      event?.HeroImageUrl,
      event?.bannerImage,
      event?.BannerImage
    ));
    const bookingUrl = this.api.eventUrl(event);
    const start = this.api.startDate(event);
    const end = this.api.endDate(event);
    const zone = this.api.timeZone(event);

    if (name) this.setText('[data-abbl-title]', name);
    if (lead) this.setText('[data-abbl-lead]', this.api.stripHtml(lead), true);
    if (description) this.setHtml('[data-abbl-description]', description);
    if (heroImage) this.setHero(heroImage, name || 'Tabletop Commons event');
    this.setBookingLink(this.api.canBook(event) ? bookingUrl : '', event);

    const facts = this.buildFacts(event, start, zone);
    if (facts.length) this.setFacts(facts);
    if (start) this.setSchedule(start, end, zone);
    if (this.hasAttribute('data-abbl-schema')) {
      this.setEventSchema(event, { name, lead, description, heroImage, bookingUrl, start, end });
    }
  }

  buildFacts(event, start, zone) {
    const facts = [];
    if (start) {
      facts.push(['Date', this.api.formatDate(start, { weekday: 'long', day: 'numeric', month: 'long' }, zone)]);
    }

    const venue = this.venueLabel(event?.venue || event?.Venue);
    if (venue) facts.push(['Venue', venue]);

    const price = this.api.priceDisplay(event);
    if (price) facts.push(['Entry', price]);

    const remaining = this.api.remainingPlaces(event);
    if (this.api.canBook(event)) {
      const availability = typeof remaining === 'number'
        ? `${remaining} ${remaining === 1 ? 'place' : 'places'} left`
        : this.api.availabilityLabel(event);
      if (availability) facts.push(['Availability', availability]);
    } else {
      facts.push(['Schedule', this.api.occurrenceLabel(event, {
        weeklyEvents: this.weeklyEvents,
        store: this.store
      })]);
    }

    return facts.slice(0, 4);
  }

  venueLabel(venue) {
    if (!venue) return '';
    if (typeof venue === 'string') return this.api.stripHtml(venue);
    return [venue.name || venue.Name, venue.suburb || venue.Suburb]
      .filter(Boolean)
      .join(', ');
  }

  setFacts(facts) {
    const list = this.querySelector('[data-abbl-facts]');
    const wrap = this.querySelector('[data-abbl-facts-wrap]');
    if (!list || !wrap) return;
    list.innerHTML = facts.map(([label, value]) => `<div><dt>${this.escapeHtml(label)}</dt><dd>${this.escapeHtml(value)}</dd></div>`).join('');
    wrap.hidden = false;
  }

  setSchedule(start, end, zone) {
    const heading = this.api.formatDate(start, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }, zone);
    const startTime = this.api.formatDate(start, { hour: 'numeric', minute: '2-digit' }, zone);
    const endTime = end && end.getTime() > start.getTime()
      ? this.api.formatDate(end, { hour: 'numeric', minute: '2-digit' }, zone)
      : '';
    this.setText('[data-abbl-schedule-heading]', heading, true);
    this.setText('[data-abbl-schedule-body]', endTime ? `${startTime}–${endTime}` : startTime, true);
    const wrap = this.querySelector('[data-abbl-schedule-wrap]');
    if (wrap) wrap.hidden = false;
  }

  setHero(url, alt) {
    const media = this.querySelector('[data-abbl-hero-media]');
    if (!media) return;
    media.innerHTML = `<img src="${this.escapeAttribute(url)}" alt="${this.escapeAttribute(alt)}" width="2200" height="1100" loading="eager" fetchpriority="high">`;
    media.hidden = false;
    const placeholder = this.querySelector('[data-abbl-hero-placeholder]');
    if (placeholder) placeholder.hidden = true;
  }

  setBookingLink(url, event) {
    const link = this.querySelector('[data-abbl-primary]');
    if (!link) return;
    if (!url) {
      link.hidden = true;
      return;
    }
    link.href = url;
    link.textContent = this.api.first(event?.bookingLabel, event?.BookingLabel) || 'Book or view details';
    link.hidden = false;
  }

  setText(selector, value, reveal = false) {
    const element = this.querySelector(selector);
    if (!element || value === undefined || value === null || value === '') return;
    element.textContent = String(value);
    if (reveal) element.hidden = false;
  }

  setHtml(selector, value) {
    const element = this.querySelector(selector);
    if (!element) return;
    element.innerHTML = this.sanitiseRichHtml(String(value));
  }

  setEventSchema(event, resolved) {
    if (!resolved.name || !resolved.start) return;
    const venue = event?.venue || event?.Venue || {};
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: resolved.name,
      startDate: resolved.start.toISOString(),
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      eventStatus: 'https://schema.org/EventScheduled',
      location: {
        '@type': 'Place',
        name: this.venueLabel(venue) || this.dataset.storeName || 'Tabletop Commons',
        address: {
          '@type': 'PostalAddress',
          streetAddress: venue.address1 || venue.Address1 || '170 Cowper Street',
          addressLocality: venue.suburb || venue.Suburb || 'Warrawong',
          addressRegion: venue.state || venue.State || 'NSW',
          postalCode: venue.postcode || venue.Postcode || '2502',
          addressCountry: venue.country || venue.Country || 'AU'
        }
      }
    };
    if (resolved.end) schema.endDate = resolved.end.toISOString();
    if (resolved.lead || resolved.description) schema.description = this.api.stripHtml(resolved.lead || resolved.description).slice(0, 500);
    if (resolved.heroImage) schema.image = [resolved.heroImage];
    if (resolved.bookingUrl) schema.url = resolved.bookingUrl;

    const amount = this.api.first(event?.price?.amount, event?.Price?.Amount);
    if (resolved.bookingUrl && amount !== undefined && amount !== null && amount !== '') {
      schema.offers = {
        '@type': 'Offer',
        url: resolved.bookingUrl,
        price: String(amount),
        priceCurrency: this.api.first(event?.price?.currency, event?.Price?.Currency, 'AUD'),
        availability: this.api.availabilityStatus(event) === 'sold_out'
          ? 'https://schema.org/SoldOut'
          : 'https://schema.org/InStock'
      };
    }

    this.querySelector('[data-abbl-event-schema]')?.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.abblEventSchema = '';
    script.textContent = JSON.stringify(schema);
    this.append(script);
  }

  sanitiseRichHtml(value) {
    const documentFragment = new DOMParser().parseFromString(value, 'text/html');
    const allowedTags = new Set(['P', 'BR', 'STRONG', 'EM', 'A', 'UL', 'OL', 'LI', 'H2', 'H3', 'H4', 'BLOCKQUOTE']);
    [...documentFragment.body.querySelectorAll('*')].forEach((element) => {
      if (!allowedTags.has(element.tagName)) {
        element.replaceWith(...element.childNodes);
        return;
      }
      const href = element.tagName === 'A' ? element.getAttribute('href') : '';
      [...element.attributes].forEach((attribute) => element.removeAttribute(attribute.name));
      if (element.tagName === 'A') {
        const safeHref = this.api.safeUrl(href, ['http:', 'https:', 'mailto:', 'tel:']);
        if (safeHref) {
          element.setAttribute('href', safeHref);
          element.setAttribute('rel', 'noopener');
        }
      }
    });
    return documentFragment.body.innerHTML;
  }

  escapeHtml(value) {
    return String(value || '').replace(/[&<>'"]/g, (character) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#039;', '"': '&quot;'
    })[character]);
  }

  escapeAttribute(value) { return this.escapeHtml(value); }
}

if (!customElements.get('tabletop-commons-program')) {
  customElements.define('tabletop-commons-program', TabletopCommonsProgram);
}
})();
